// src/hooks/useWakeLock.js
//
// Haelt den Bildschirm an, solange `aktiv` wahr ist (Screen Wake Lock API).
// Das haelt NUR das Display an, solange die Seite im Vordergrund ist – ein
// Web-App kann nicht erzwingen, dass sie vorn bleibt. Der Lock wird beim
// Wechsel in den Hintergrund vom Browser selbst freigegeben; kehrt man zurueck
// und es ist noch `aktiv`, fordern wir ihn neu an. Unkritisch fuer die
// Genauigkeit von Stoppuhr/Countdown: gemessen wird ueber Zeitstempel.
//
// Gibt zurueck, ob der Lock gerade tatsaechlich gehalten wird.

import { useEffect, useRef, useState } from "react";

export default function useWakeLock(aktiv) {
  const wakeLockRef = useRef(null);
  const [bildschirmWach, setBildschirmWach] = useState(false);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("wakeLock" in navigator)) return;

    let abgebrochen = false;

    const anfordern = async () => {
      if (!aktiv || document.visibilityState !== "visible") return;
      if (wakeLockRef.current) return;
      try {
        const lock = await navigator.wakeLock.request("screen");
        if (abgebrochen) {
          lock.release().catch(() => {});
          return;
        }
        wakeLockRef.current = lock;
        setBildschirmWach(true);
        // Browser gibt den Lock z. B. beim Sperren selbst frei – Referenz
        // aufraeumen, damit ein spaeterer Versuch wieder greifen kann.
        lock.addEventListener("release", () => {
          wakeLockRef.current = null;
          setBildschirmWach(false);
        });
      } catch {
        // Verweigert (Akkusparmodus, kein Nutzergesten-Kontext o. Ae.) –
        // bewusst ignoriert, die Uhr laeuft trotzdem korrekt weiter.
      }
    };

    const freigeben = () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
      setBildschirmWach(false);
    };

    const beiSichtbarkeit = () => {
      if (document.visibilityState === "visible") anfordern();
    };

    if (aktiv) {
      anfordern();
      document.addEventListener("visibilitychange", beiSichtbarkeit);
    } else {
      freigeben();
    }

    return () => {
      abgebrochen = true;
      document.removeEventListener("visibilitychange", beiSichtbarkeit);
      freigeben();
    };
  }, [aktiv]);

  return bildschirmWach;
}
